import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type {
  ServiceHealthState,
  CustomerEnvironment,
  HealthFilter,
} from "../types/serviceHealth.types";
import { serviceHealthService } from "../services/serviceHealthService";

export const DEFAULT_FILTERS: ServiceHealthState["filters"] = {
  healthFilter: "All",
  search: "",
  selectedUseCases: [],
  selectedCustomers: [],
};

const createDefaultFilters = (): ServiceHealthState["filters"] => ({
  ...DEFAULT_FILTERS,
  selectedUseCases: [...DEFAULT_FILTERS.selectedUseCases],
  selectedCustomers: [...DEFAULT_FILTERS.selectedCustomers],
});

const initialState: ServiceHealthState = {
  customerEnvironments: [],
  lastRefreshedTime: null,
  error: null,
  status: {
    customerEnvironments: "idle",
  },
  filters: createDefaultFilters(),
  filtersInitialised: true,
};

export const fetchCustomerEnvironments = createAsyncThunk<
  CustomerEnvironment[],
  number | undefined,
  {
    state: RootState;
    rejectValue: string;
  }
>(
  "serviceHealthStore/fetchCustomerEnvironments",
  async (top: number = 100, { rejectWithValue }) => {
    try {
      return await serviceHealthService.getCustomerEnvironments(top);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch service health customer environments";
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (_top, { getState }) => {
      const state = getState() as RootState;
      return state.serviceHealth.status.customerEnvironments !== "loading";
    },
  },
);

const serviceHealthSlice = createSlice({
  name: "serviceHealth",
  initialState,
  reducers: {
    setHealthFilter(state, action: PayloadAction<HealthFilter>) {
      state.filters.healthFilter = action.payload;
    },
    setSearch(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setSelectedUseCases(state, action: PayloadAction<string[]>) {
      state.filters.selectedUseCases = action.payload;
    },
    setSelectedCustomers(state, action: PayloadAction<string[]>) {
      state.filters.selectedCustomers = action.payload;
    },
    clearFilters(state) {
      state.filters = createDefaultFilters();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerEnvironments.pending, (state) => {
        state.status.customerEnvironments = "loading";
        state.error = null;
      })
      .addCase(fetchCustomerEnvironments.fulfilled, (state, action) => {
        state.customerEnvironments = action.payload;
        state.status.customerEnvironments = "succeeded";
        state.lastRefreshedTime = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchCustomerEnvironments.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.status.customerEnvironments = "failed";
        state.error =
          action.payload ?? action.error.message ?? "Failed to fetch customer environments";
      });
  },
});

export const selectCustomerEnvironments = (state: RootState) =>
  state.serviceHealth.customerEnvironments;
export const selectServiceHealthLoadStatus = (state: RootState) =>
  state.serviceHealth.status.customerEnvironments;
export const selectServiceHealthFilters = (state: RootState) =>
  state.serviceHealth.filters;
export const selectServiceHealthLastRefreshed = (state: RootState) =>
  state.serviceHealth.lastRefreshedTime;
export const selectServiceHealthError = (state: RootState) => state.serviceHealth.error;

export const { setHealthFilter, setSearch, setSelectedUseCases, setSelectedCustomers, clearFilters } =
  serviceHealthSlice.actions;
export const serviceHealthReducer = serviceHealthSlice.reducer;
export default serviceHealthReducer;
